// Cliente del modulo Condominios (spec 008). No agregar estas rutas a api.ts.
// El tenant sale del JWT; condo_id en la ruta solo selecciona el condominio y el
// backend vuelve a autorizar cada request.
import { isAxiosError } from 'axios';
import { apiClient } from '../apiClient';
import type { components } from './condo.types';

type S = components['schemas'];
export type CondoRole = S['CondoRole'];
export type Permission = S['Permission'];
export type MyCondo = S['MyCondo'];
export type MyPermissions = S['MyPermissions'];
export type Condo = S['Condo'];
export type Unit = S['Unit'];
/** 0.3.8: respuesta de POST /units (Unit + warnings + indiviso_total + indiviso_complete). */
export type UnitWrite = S['UnitWrite'];
export type ApiWarning = S['Warning'];
export type UnitPage = S['UnitPage'];
export type UnitCreate = S['UnitCreate'];
export type UnitUpdate = S['UnitUpdate'];
export type ImportJob = S['ImportJob'];
export type ImportError = NonNullable<ImportJob['errors']>[number];
export type IndivisoSummary = S['IndivisoSummary'];
export type IndivisoChangeResult = S['IndivisoChangeResult'];
/** PUT /units/indiviso (lote atomico). Esquema de a534c52 (IndivisoBatch); el yaml 0.3.8 aun no lo define. */
export interface IndivisoBatchIn {
  effective_from_period: string;
  items: { unit_id: string; indiviso: string }[];
}
export type Membership = S['Membership'];
export type Invitation = S['Invitation'];
export type InvitationPage = S['InvitationPage'];
export type InvitationStatus = S['InvitationStatus'];
export type ErrorCode = S['ErrorCode'];
export type BusinessType = S['BusinessType'];
export type CondoUpdate = S['CondoUpdate'];
export type CondoConfig = S['CondoConfig'];
export type FeeSchedule = S['FeeSchedule'];
export type FeeScheduleIn = S['FeeScheduleIn'];
export type FeeSchedulePatch = S['FeeSchedulePatch'];
export type AdminNotice = S['AdminNotice'];
export type ExtraordinaryIn = S['ExtraordinaryIn'];
export type ProrationPreview = S['ProrationPreview'];
export type SurchargeRule = S['SurchargeRule'];
export type Statement = Omit<S['Statement'], 'lines'> & { lines?: StatementLine[] };
/** charge_id: necesario para anular; el yaml 0.3.8 y a534c52 aun no lo devuelven (hueco reportado a Backend). */
export type StatementLine = S['StatementLine'] & { charge_id?: string | null };
export type AgingBucket = S['AgingBucket'];
export type DelinquencyReport = S['DelinquencyReport'];
export type DelinquencyRow = NonNullable<DelinquencyReport['items']>[number];
/** has_receipt: 0.3.9 (aun no en yaml ni a534c52). Ausente = desconocido: se ofrece la descarga y un 404 se informa. */
export type Payment = S['Payment'] & { has_receipt?: boolean | null };
export type PaymentPage = S['PaymentPage'];

/** Respuesta de POST /charges/{id}/void segun a534c52 (el yaml no define esquema para el 200). */
export interface VoidedCharge {
  id: string;
  status: 'void';
  amount?: string;
  concept?: string;
  kind?: string;
  period?: string | null;
  void_reason?: string;
  voided_at?: string;
}
export const VOID_REASON_MIN = 5;
export const VOID_REASON_MAX = 500;

export interface ManualPaymentIn {
  unit_id: string;
  amount: string;
  method: 'cash' | 'transfer';
  received_at: string;
  reference?: string;
  receipt?: File | null;
}

/** Idempotency-Key por intento logico: se genera una vez y se reutiliza en reintentos. */
export function newIdempotencyKey(): string {
  return crypto.randomUUID();
}
export type InvitationCreate = S['InvitationCreate'];
export type CondoApiError = S['Error'];
export type Me = S['Me'];
export type MeUpdate = S['MeUpdate'];
export type Locale = S['Locale'];

const base = (condoId: string) => `/api/condos/${encodeURIComponent(condoId)}`;

export interface InvitationListParams {
  unitId?: string;
  status?: InvitationStatus;
  /** 0.3.8: 'email' lista las invitaciones sin correo (solo telefono). */
  missingChannel?: 'email';
  importJobId?: string;
  cursor?: string | null;
  limit?: number;
}

export type InvitationSendBody = { invitation_ids?: string[]; import_job_id?: string };
export interface InvitationSendResult {
  sent?: number;
  failed?: { invitation_id?: string; code: 'NOT_DRAFT' | 'INVITATION_NO_EMAIL' | 'INVALID_EMAIL' }[];
}

export interface UnitListParams {
  cursor?: string | null;
  limit?: number;
  q?: string;
  delinquent?: boolean;
}

/** Error normalizado: code (ErrorCode del contrato, o NETWORK/HTTP_<status>/UNKNOWN locales) + params.
 *  Mismo contrato que apiErrorMessage (T1): el `detail` del servidor nunca se lee ni se guarda. */
export interface CondoError {
  status: number;
  code: ErrorCode | 'NETWORK' | 'UNKNOWN' | `HTTP_${number}`;
  params: Record<string, unknown>;
}

export function toCondoError(err: unknown): CondoError {
  if (isAxiosError(err)) {
    const data = err.response?.data as Partial<CondoApiError> | undefined;
    return {
      status: err.response?.status ?? 0,
      code: data?.code || (err.response ? `HTTP_${err.response.status}` : 'NETWORK'),
      params: data?.params ?? {},
    };
  }
  return { status: 0, code: 'UNKNOWN', params: {} };
}

const idem = (key: string) => ({ 'Idempotency-Key': key });

export const condoBillingApi = {
  async updateCondo(condoId: string, body: CondoUpdate): Promise<Condo> {
    return (await apiClient.patch<Condo>(base(condoId), body)).data;
  },
  async listFeeSchedules(condoId: string): Promise<FeeSchedule[]> {
    return (await apiClient.get<FeeSchedule[]>(`${base(condoId)}/fee-schedules`)).data;
  },
  async createFeeSchedule(condoId: string, body: FeeScheduleIn): Promise<FeeSchedule> {
    return (await apiClient.post<FeeSchedule>(`${base(condoId)}/fee-schedules`, body)).data;
  },
  /** Solo activa/desactiva (0.3.8). Aplica a periodos que el job aun no genera; lo ya cobrado no cambia. */
  async updateFeeSchedule(condoId: string, scheduleId: string, body: FeeSchedulePatch): Promise<FeeSchedule> {
    return (await apiClient.patch<FeeSchedule>(`${base(condoId)}/fee-schedules/${scheduleId}`, body)).data;
  },
  /** Avisos al admin generados por jobs (ultimos 100, sin paginacion). */
  /** 0.3.9: GET /notices paginado (cursor/limit). Tolera el arreglo simple actual (pagina unica). */
  async listNotices(condoId: string, params: { cursor?: string | null; limit?: number } = {}): Promise<NoticePage> {
    const query: Record<string, string | number> = { limit: params.limit ?? 50 };
    if (params.cursor) query.cursor = params.cursor;
    return normalizePage((await apiClient.get<NoticePage | AdminNotice[]>(`${base(condoId)}/notices`, { params: query })).data);
  },
  async getSurchargeRule(condoId: string): Promise<SurchargeRule> {
    return (await apiClient.get<SurchargeRule>(`${base(condoId)}/surcharge-rules`)).data;
  },
  async putSurchargeRule(condoId: string, body: SurchargeRule): Promise<SurchargeRule> {
    return (await apiClient.put<SurchargeRule>(`${base(condoId)}/surcharge-rules`, body)).data;
  },
  async previewExtraordinary(condoId: string, body: ExtraordinaryIn): Promise<ProrationPreview> {
    return (await apiClient.post<ProrationPreview>(`${base(condoId)}/charges/extraordinary/preview`, body)).data;
  },
  async createExtraordinary(condoId: string, body: ExtraordinaryIn, idempotencyKey: string): Promise<ProrationPreview> {
    return (await apiClient.post<ProrationPreview>(`${base(condoId)}/charges/extraordinary`, body, { headers: idem(idempotencyKey) })).data;
  },
  async voidCharge(condoId: string, chargeId: string, reason: string): Promise<VoidedCharge> {
    return (await apiClient.post<VoidedCharge>(`${base(condoId)}/charges/${chargeId}/void`, { reason })).data;
  },
  async statement(condoId: string, unitId: string, range: { from?: string; to?: string } = {}): Promise<Statement> {
    return (await apiClient.get<Statement>(`${base(condoId)}/units/${unitId}/statement`, { params: range })).data;
  },
  /** PDF generado por el servidor. */
  async statementPdf(condoId: string, unitId: string, range: { from?: string; to?: string } = {}): Promise<Blob> {
    return (await apiClient.get<Blob>(`${base(condoId)}/units/${unitId}/statement.pdf`, { params: range, responseType: 'blob' })).data;
  },
  async delinquency(condoId: string, params: { bucket?: AgingBucket; cursor?: string | null; limit?: number } = {}): Promise<DelinquencyReport> {
    const query: Record<string, string | number> = { limit: params.limit ?? 100 };
    if (params.bucket) query.bucket = params.bucket;
    if (params.cursor) query.cursor = params.cursor;
    return (await apiClient.get<DelinquencyReport>(`${base(condoId)}/delinquency`, { params: query })).data;
  },
  async delinquencyCsv(condoId: string, bucket?: AgingBucket): Promise<Blob> {
    return (await apiClient.get<Blob>(`${base(condoId)}/delinquency.csv`, { params: bucket ? { bucket } : {}, responseType: 'blob' })).data;
  },
  async createManualPayment(condoId: string, body: ManualPaymentIn, idempotencyKey: string): Promise<Payment> {
    const form = new FormData();
    form.append('unit_id', body.unit_id);
    form.append('amount', body.amount);
    form.append('method', body.method);
    form.append('received_at', body.received_at);
    if (body.reference) form.append('reference', body.reference);
    if (body.receipt) form.append('receipt', body.receipt, body.receipt.name);
    return (await apiClient.post<Payment>(`${base(condoId)}/payments/manual`, form, {
      headers: { ...idem(idempotencyKey), 'Content-Type': false },
    })).data;
  },
  async listUnidentified(condoId: string, params: { cursor?: string | null; limit?: number } = {}): Promise<PaymentPage> {
    const query: Record<string, string | number> = { limit: params.limit ?? 50 };
    if (params.cursor) query.cursor = params.cursor;
    return (await apiClient.get<PaymentPage>(`${base(condoId)}/payments/unidentified`, { params: query })).data;
  },
  async getPayment(condoId: string, paymentId: string): Promise<Payment> {
    return (await apiClient.get<Payment>(`${base(condoId)}/payments/${paymentId}`)).data;
  },
  /** Comprobante via stream autorizado (no-store). 404 = sin comprobante; 502 STORAGE_UNAVAILABLE. */
  async paymentReceipt(condoId: string, paymentId: string): Promise<Blob> {
    return (await apiClient.get<Blob>(`${base(condoId)}/payments/${paymentId}/receipt`, { responseType: 'blob' })).data;
  },
  /** 0.3.9: Idempotency-Key obligatoria (misma llave + otro cuerpo -> 409 IDEMPOTENCY_KEY_REUSED). */
  async assignPayment(condoId: string, paymentId: string, unitId: string, note: string, idempotencyKey: string = newIdempotencyKey()): Promise<Payment> {
    return (await apiClient.post<Payment>(`${base(condoId)}/payments/${paymentId}/assign`, { unit_id: unitId, note }, { headers: idem(idempotencyKey) })).data;
  },
};

export const condoApi = {
  /** /me.locale es la unica fuente de idioma (web, movil, push, email). */
  async getMe(): Promise<Me> {
    return (await apiClient.get<Me>('/api/me')).data;
  },
  async updateMe(body: MeUpdate): Promise<Me> {
    return (await apiClient.patch<Me>('/api/me', body)).data;
  },
  async myCondos(): Promise<MyCondo[]> {
    return (await apiClient.get<MyCondo[]>('/api/me/condos')).data;
  },
  async myPermissions(condoId: string): Promise<MyPermissions> {
    return (await apiClient.get<MyPermissions>(`${base(condoId)}/me/permissions`)).data;
  },
  async getCondo(condoId: string): Promise<Condo> {
    return (await apiClient.get<Condo>(base(condoId))).data;
  },
  async listUnits(condoId: string, params: UnitListParams = {}): Promise<UnitPage> {
    const query: Record<string, string | number | boolean> = { limit: params.limit ?? 50 };
    if (params.cursor) query.cursor = params.cursor;
    if (params.q) query.q = params.q;
    if (params.delinquent !== undefined) query.delinquent = params.delinquent;
    return normalizeUnitPage((await apiClient.get<UnitPage | Unit[]>(`${base(condoId)}/units`, { params: query })).data);
  },
  async getUnit(condoId: string, unitId: string): Promise<Unit> {
    return (await apiClient.get<Unit>(`${base(condoId)}/units/${unitId}`)).data;
  },
  async createUnit(condoId: string, body: UnitCreate): Promise<UnitWrite> {
    const payload = { ...body, owner_email: body.owner_email ? normalizeEmail(body.owner_email) : undefined };
    return (await apiClient.post<Unit>(`${base(condoId)}/units`, payload)).data;
  },
  async updateUnit(condoId: string, unitId: string, body: UnitUpdate): Promise<Unit> {
    return (await apiClient.patch<Unit>(`${base(condoId)}/units/${unitId}`, body)).data;
  },
  async setIndiviso(condoId: string, unitId: string, indiviso: string, effectiveFromPeriod: string): Promise<void> {
    await apiClient.put(`${base(condoId)}/units/${unitId}/indiviso`, { indiviso, effective_from_period: effectiveFromPeriod });
  },
  /** Baja logica (status=inactive); el historial se conserva. Backend a534c52, pendiente en el yaml. */
  async setIndivisoBatch(condoId: string, body: IndivisoBatchIn): Promise<IndivisoChangeResult> {
    return (await apiClient.put<IndivisoChangeResult>(`${base(condoId)}/units/indiviso`, body)).data;
  },
  async deactivateUnit(condoId: string, unitId: string): Promise<Unit> {
    return (await apiClient.post<Unit>(`${base(condoId)}/units/${unitId}/deactivate`)).data;
  },
  async indivisoSummary(condoId: string): Promise<IndivisoSummary> {
    return (await apiClient.get<IndivisoSummary>(`${base(condoId)}/units/indiviso-summary`)).data;
  },
  /** dry_run=true valida sin escribir. El import real exige el job_id de un dry-run sin errores. */
  async importUnits(condoId: string, file: File, opts: { dryRun: true } | { dryRun: false; dryRunJobId: string }): Promise<ImportJob> {
    const form = new FormData();
    form.append('file', file);
    const params: Record<string, string | boolean> = { dry_run: opts.dryRun };
    if (!opts.dryRun) params.dry_run_job_id = opts.dryRunJobId;
    return (await apiClient.post<ImportJob>(`${base(condoId)}/units/import`, form, {
      params,
      // Sin Content-Type explicito: el navegador agrega el boundary del multipart.
      headers: { 'Content-Type': false },
    })).data;
  },
  async getImportJob(condoId: string, jobId: string): Promise<ImportJob> {
    return (await apiClient.get<ImportJob>(`${base(condoId)}/units/import/${jobId}`)).data;
  },
  /**
   * GET /memberships. 0.3.9: full_name, unit_code, filtro ?unit_id y cursor. Tolera el arreglo simple de
   * a534c52 (first_name/last_name, sin filtro): se trata como pagina unica y se filtra por unidad aqui.
   */
  async listMembershipsPage(condoId: string, params: { unitId?: string; cursor?: string | null; limit?: number } = {}): Promise<MembershipPage> {
    const query: Record<string, string | number> = { limit: params.limit ?? 50 };
    if (params.unitId) query.unit_id = params.unitId;
    if (params.cursor) query.cursor = params.cursor;
    const page = normalizePage((await apiClient.get<MembershipPage | MembershipOut[]>(`${base(condoId)}/memberships`, { params: query })).data);
    return { ...page, items: page.items.map(normalizeMembership).filter((m) => !params.unitId || !m.unit_id || m.unit_id === params.unitId) };
  },
  async listMemberships(condoId: string, params: { unitId?: string } = {}): Promise<MembershipOut[]> {
    return (await condoApi.listMembershipsPage(condoId, params)).items;
  },
  async revokeMembership(condoId: string, membershipId: string): Promise<void> {
    await apiClient.delete(`${base(condoId)}/memberships/${membershipId}`);
  },
  async invite(condoId: string, body: InvitationCreate): Promise<Invitation> {
    return (await apiClient.post<Invitation>(`${base(condoId)}/invitations`, { ...body, email: normalizeEmail(body.email) })).data;
  },
  async listInvitations(condoId: string, params: InvitationListParams = {}): Promise<InvitationPage> {
    const query: Record<string, string | number> = { limit: params.limit ?? 50 };
    if (params.cursor) query.cursor = params.cursor;
    if (params.unitId) query.unit_id = params.unitId;
    if (params.status) query.status = params.status;
    if (params.missingChannel) query.missing_channel = params.missingChannel;
    if (params.importJobId) query.import_job_id = params.importJobId;
    return (await apiClient.get<InvitationPage>(`${base(condoId)}/invitations`, { params: query })).data;
  },
  /** 0.3.8: envia invitaciones draft (por ids o import_job_id). Las fallidas vienen en failed[]. */
  async sendInvitations(condoId: string, body: InvitationSendBody, idempotencyKey: string): Promise<InvitationSendResult> {
    return (await apiClient.post<InvitationSendResult>(`${base(condoId)}/invitations/send`, body, { headers: { 'Idempotency-Key': idempotencyKey } })).data;
  },
  /** 0.3.8: plantilla CSV del servidor segun locale (es-MX: alias en espanol; en-US: canonicos). */
  async importTemplate(condoId: string, locale: Locale): Promise<Blob> {
    return (await apiClient.get<Blob>(`${base(condoId)}/units/import/template`, { params: { locale }, responseType: 'blob' })).data;
  },
  /** Reenvio (pending/expired): nuevo token y 7 dias de vigencia. Si SMTP falla responde 200 con email_status=failed. */
  async resendInvitation(condoId: string, invitationId: string): Promise<Invitation> {
    return (await apiClient.post<Invitation>(`${base(condoId)}/invitations/${invitationId}/resend`)).data;
  },
  async revokeInvitation(condoId: string, invitationId: string): Promise<void> {
    await apiClient.delete(`${base(condoId)}/invitations/${invitationId}`);
  },
};

/** Membership 0.3.9 (full_name, unit_id, unit_code). first_name/last_name: backend previo (a534c52). `name` es solo de UI
 *  (nombre a mostrar derivado en normalizeMembership); no viene del contrato. */
export type MembershipOut = Partial<Membership> & { first_name?: string | null; last_name?: string | null; name?: string | null };
export interface MembershipPage { items: MembershipOut[]; next_cursor: string | null }
export interface NoticePage { items: AdminNotice[]; next_cursor: string | null }

export function normalizeMembership(m: MembershipOut): MembershipOut {
  const full = [m.first_name, m.last_name].filter(Boolean).join(' ').trim();
  return { ...m, name: m.full_name || m.name || full || undefined };
}

/** Acepta {items, next_cursor} del contrato o un arreglo simple (pagina unica). */
export function normalizePage<T>(data: { items?: T[]; next_cursor?: string | null } | T[] | null | undefined): { items: T[]; next_cursor: string | null } {
  if (Array.isArray(data)) return { items: data, next_cursor: null };
  return { items: data?.items ?? [], next_cursor: data?.next_cursor ?? null };
}

/**
 * Temporal (decision de Producto, hasta Backend 0.3.9): GET /units puede responder el arreglo
 * simple de a534c52 o la UnitPage del contrato. Un arreglo se trata como pagina unica.
 */
export function normalizeUnitPage(data: UnitPage | Unit[] | null | undefined): UnitPage {
  if (Array.isArray(data)) return { items: data, next_cursor: null };
  return { items: data?.items ?? [], next_cursor: data?.next_cursor ?? null };
}

export function normalizeEmail(email: string): string {
  return email.trim().toLowerCase();
}

// ---------------------------------------------------------------- Fase C (pagos en linea)
// Detras de VITE_CONDO_ONLINE_PAYMENTS (apagado por defecto). El monto lo define el backend:
// la web nunca manda amount; paga el saldo pendiente o charge_ids. Web = Stripe Checkout hospedado (checkout_url).
export type PayMethod = 'card' | 'oxxo' | 'spei';
/** Para web solo se leen pay_intent_id, method, checkout_url, clabe/bank_name, reference, oxxo_voucher_url y expires_at. */
export type PayIntent = Omit<S['PayIntent'], 'client_secret' | 'payment_sheet'> & { bank_name?: string | null; oxxo_reference?: string | null };
export type PayPreview = S['PayPreview'];
export type MethodBreakdown = S['MethodBreakdown'];
export type ConnectStatus = S['ConnectStatus'];
/** yaml 0.3.9 PayIntentStatus.status (+ failed de condo_pay_intents en 18bc66d). Los cambia el webhook; la web consulta. */
export type PayIntentStatus = 'pending' | 'processing' | 'succeeded' | 'failed' | 'canceled' | 'expired';
/** yaml 0.3.9 PayIntentStatus (GET /pay-intents/{id}). condo.types.ts aun es 0.3.8: tipo a mano. */
export interface PayIntentState {
  id: string;
  status: PayIntentStatus;
  method: PayMethod;
  amount?: string;
  payment_id?: string | null;
  expires_at?: string | null;
  updated_at?: string;
  error_code?: string | null;
}
/** stripe_jobs.py (backend): diffs del job diario de conciliacion. El yaml solo dice `object`. */
export interface ReconciliationDiff {
  kind: 'missing_in_ledger' | 'amount_mismatch' | 'missing_in_stripe' | string;
  stripe_source?: string | null;
  payment_id?: string | null;
  amount?: string | null;
  stripe_amount?: string | null;
  ledger_amount?: string | null;
}
export interface Reconciliation {
  date: string;
  status?: 'not_run' | 'ok' | 'diffs' | string;
  diffs: ReconciliationDiff[];
  stripe_count?: number;
  ledger_count?: number;
  created_at?: string;
}

export const condoPayApi = {
  async preview(condoId: string, unitId: string, body: { charge_ids?: string[] } = {}): Promise<PayPreview> {
    return (await apiClient.post<PayPreview>(`${base(condoId)}/units/${unitId}/pay/preview`, body)).data;
  },
  /** POST /pay: Idempotency-Key obligatoria; sin amount (lo calcula el backend). */
  async pay(condoId: string, unitId: string, body: { method: PayMethod; charge_ids?: string[] }, idempotencyKey: string): Promise<PayIntent> {
    return (await apiClient.post<PayIntent>(`${base(condoId)}/units/${unitId}/pay`, { ...body, client: 'web' }, { headers: idem(idempotencyKey) })).data;
  },
  /** yaml 0.3.9: GET /condos/{condo_id}/pay-intents/{id} (rate limit 30/min por usuario). */
  async intentStatus(condoId: string, payIntentId: string): Promise<PayIntentState> {
    const d = (await apiClient.get<PayIntentState & { pay_intent_id?: string; total?: string }>(`${base(condoId)}/pay-intents/${payIntentId}`)).data;
    return { ...d, id: d.id ?? d.pay_intent_id ?? payIntentId, amount: d.amount ?? d.total };
  },
  async connectStatus(condoId: string): Promise<ConnectStatus> {
    return (await apiClient.get<ConnectStatus>(`${base(condoId)}/payments/connect/status`)).data;
  },
  async reconciliation(condoId: string, date?: string): Promise<Reconciliation> {
    const data = (await apiClient.get<Reconciliation>(`${base(condoId)}/reconciliation`, { params: date ? { date } : {} })).data;
    return { ...data, diffs: data?.diffs ?? [] };
  },
};

// ---------------------------------------------------------------- Comprobantes (PaymentProof, yaml 0.3.9)
// condo.types.ts aun es 0.3.8: tipos a mano segun el yaml.
export type ProofStatus = 'pending' | 'approved' | 'rejected';
export interface PaymentProof {
  id: string;
  unit_id: string;
  amount: string;
  paid_at: string;
  reference?: string | null;
  note?: string | null;
  status: ProofStatus;
  content_type: 'image/jpeg' | 'image/png' | 'application/pdf';
  filename?: string | null;
  size_bytes?: number;
  uploaded_by?: string | null;
  created_at: string;
  reviewed_by?: string | null;
  reviewed_at?: string | null;
  reject_reason?: string | null;
  payment_id?: string | null;
}
export interface PaymentProofPage { items: PaymentProof[]; next_cursor: string | null }
export const PROOF_REASON_MIN = 3;
export const PROOF_REASON_MAX = 500;

export const condoProofsApi = {
  /**
   * Con unidad: GET /units/{unit_id}/payment-proofs (yaml). Sin unidad: HUECO, el yaml no tiene listado del condominio;
   * se usa la propuesta GET /condos/{condo_id}/payment-proofs?status&unit_id&cursor (solo en mocks).
   */
  async list(condoId: string, params: { status?: ProofStatus; unitId?: string; cursor?: string | null; limit?: number } = {}): Promise<PaymentProofPage> {
    const query: Record<string, string | number> = { limit: params.limit ?? 50 };
    if (params.status) query.status = params.status;
    if (params.cursor) query.cursor = params.cursor;
    const url = params.unitId ? `${base(condoId)}/units/${params.unitId}/payment-proofs` : `${base(condoId)}/payment-proofs`;
    return normalizePage((await apiClient.get<PaymentProofPage | PaymentProof[]>(url, { params: query })).data);
  },
  /** yaml 0.3.9: GET /payment-proofs/{id}/file. Proxy autenticado no-store; el backend lo sirve como adjunto
   *  (Content-Disposition: attachment + CSP sandbox), asi que la UI ofrece descargarlo y nunca lo abre en otra pestana. */
  async file(condoId: string, proofId: string): Promise<Blob> {
    return (await apiClient.get<Blob>(`${base(condoId)}/payment-proofs/${proofId}/file`, { responseType: 'blob' })).data;
  },
  async approve(condoId: string, proofId: string): Promise<PaymentProof & { payment?: Payment }> {
    return (await apiClient.post<PaymentProof & { payment?: Payment }>(`${base(condoId)}/payment-proofs/${proofId}/approve`)).data;
  },
  async reject(condoId: string, proofId: string, reason: string): Promise<PaymentProof> {
    return (await apiClient.post<PaymentProof>(`${base(condoId)}/payment-proofs/${proofId}/reject`, { reason })).data;
  },
};
